import { decode } from 'html-entities';
import { computed, unref, markRaw } from 'vue';
import { useLabelsStore } from '@stores/labels';
import { getDayjsByUnix } from '@utils/date';

import CONCERT_FINDER_DATA from '@/components/organisms/concert-finder/concert-finder-data';
import { useConcertFinderI18n } from '@/components/organisms/concert-finder/use-concert-finder-i18n';

// const timezoneLocal = dayjs.tz.guess();
// const showTimezoneIndicator = TIMEZONE_SERVER !== timezoneLocal;

const concertFinderTypeMapEpublisher = Object.entries(
  CONCERT_FINDER_DATA.types
).reduce((acc, [key, type]) => {
  acc[type.epublisherType] = key;
  return acc;
}, {});

export function useExtension({ data }) {
  const meta = computed(() => {
    return unref(data)?.meta;
  });
  const teaser = computed(() => {
    return unref(data)?.teaser;
  });
  const extension = computed(() => {
    return unref(data)?.extension;
  });
  const isExternal = computed(() => {
    return teaser.value?.link?.target === 'external';
  });
  const title = computed(() => {
    if (!teaser.value?.title) return undefined;
    return decode(teaser.value.title);
  });
  const description = computed(() => {
    if (!extension.value?.description) return undefined;
    return decode(extension.value.description);
  });

  return {
    meta,
    teaser,
    extension,
    isExternal,
    title,
    description,
  };
}

export function useExtensionDate({ data }) {
  const store = useLabelsStore();

  const dayjsDate = computed(() =>
    markRaw(getDayjsByUnix(unref(data)?.extension?.date))
  );

  const date = computed(() => {
    return dayjsDate.value.format(
      store.activeLanguage === 'de' ? 'D.M.' : 'M/D'
    );
  });
  const time = computed(() => {
    return dayjsDate.value.format(
      store.activeLanguage === 'de' ? 'HH.mm [Uhr]' : 'h:mm a'
    );
  });
  const weekday = computed(() => {
    return dayjsDate.value.format('dddd');
  });
  const datetime = computed(() => {
    return dayjsDate.value.format('YYYY-MM-DDTHH:mm');
  });

  return { date, time, weekday, datetime };
}

export function useExtensionConcertFinder({ data }) {
  const { t, locale } = useConcertFinderI18n();

  const extension = computed(() => {
    return unref(data)?.extension;
  });

  const id = computed(() => {
    const epublisherType =
      extension.value?.finder?.[0]?.type ||
      extension.value?.finder?.type ||
      extension.value?.finder;

    if (!epublisherType) return undefined;

    return concertFinderTypeMapEpublisher[epublisherType];
  });

  const label = computed(() => {
    if (!id.value) return undefined;

    return t(`types.${id.value}.heading`);
  });

  const icon = computed(() => {
    if (!id.value) return undefined;

    return CONCERT_FINDER_DATA.types[id.value].icon;
  });

  return {
    id,
    label,
    icon,
  };
}
